import { cn } from 'lib/utils'
import { Check } from 'lucide-react'
import type { WeeklyQueueItem } from '../../store/weekly-queue-store'

export interface DayTileProps {
  date: string
  dayOffset: number
  items: WeeklyQueueItem[]
  expectedCount: number
  selected: boolean
  onSelect: (date: string) => void
}

const DAY_LABEL_FMT: Intl.DateTimeFormatOptions = { weekday: 'short' }

type TileState =
  'needs_review' | 'approved' | 'live' | 'skipped' | 'drafting' | 'expected' | 'empty'

function resolveTileState(items: WeeklyQueueItem[], expectedCount: number): TileState {
  if (items.some(it => it.status === 'generating')) return 'drafting'
  const livable = items.filter(it => it.status === 'scheduled' || it.status === 'published')
  if (livable.length > 0) {
    return livable.every(it => it.status === 'published') ? 'live' : 'approved'
  }
  if (items.some(it => it.status === 'failed')) return 'skipped'
  if (expectedCount > 0) return 'expected'
  return 'empty'
}

function formatDayNumber(iso: string): string {
  return String(new Date(iso).getUTCDate())
}

function formatDayLabel(iso: string): string {
  // Always show the 3-char weekday name. The previous "Today" special-case
  // made the today tile visibly wider than its neighbours (5 chars vs 3),
  // and the today identifier already lives in the acid hairline border +
  // text-acid weekday colouring. The label can stay parallel.
  return new Date(iso).toLocaleDateString('en-US', { ...DAY_LABEL_FMT, timeZone: 'UTC' })
}

function formatTodayAriaPrefix(iso: string): string {
  return `Today, ${new Date(iso).toLocaleDateString('en-US', { ...DAY_LABEL_FMT, timeZone: 'UTC' })}`
}

function ariaStatus(state: TileState, pendingCount: number, readyCount: number): string {
  switch (state) {
    case 'needs_review':
      return pendingCount === 1 ? '1 pending review' : `${pendingCount} pending review`
    case 'approved':
      return readyCount === 1 ? '1 ready' : `${readyCount} ready`
    case 'live':
      return readyCount === 1 ? '1 live' : `${readyCount} live`
    case 'drafting':
      return 'drafting'
    case 'expected':
      return 'autopilot scheduled'
    case 'skipped':
      return 'skipped'
    case 'empty':
      return 'nothing planned'
  }
}

export function DayTile({
  date,
  dayOffset,
  items,
  expectedCount,
  selected,
  onSelect,
}: DayTileProps) {
  const isToday = dayOffset === 0
  const pendingCount = items.filter(it => it.status === 'generating').length
  const readyCount = items.filter(
    it => it.status === 'scheduled' || it.status === 'published'
  ).length
  const state = resolveTileState(items, expectedCount)

  // Tile chrome per state. T3 idiom: edges over fills, restraint on acid.
  // The earlier "tint the bg with the indicator color" trick worked on
  // dark (subtle, atmospheric) but went muddy on light (acid + white =
  // chartreuse, amber + white = brown). Each state now lives in the
  // corner indicator (badge, check, livedot) — the tile chrome stays
  // neutral so the indicators carry the message clean.
  const accentClasses: Record<TileState, string> = {
    needs_review: 'border-line-bright bg-bg-3',
    approved: 'border-line-bright bg-bg-3',
    live: 'border-line-bright bg-bg-3',
    skipped: 'border-line bg-bg-2',
    drafting: 'border-line-bright bg-bg-3',
    expected: 'border-line bg-bg-2',
    empty: 'border-line bg-bg-2',
  }

  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      aria-label={`${isToday ? formatTodayAriaPrefix(date) : formatDayLabel(date)} ${formatDayNumber(date)}, ${ariaStatus(state, pendingCount, readyCount)}`}
      onClick={() => onSelect(date)}
      className={cn(
        'relative flex h-[88px] flex-col items-stretch justify-between rounded-[10px] border px-2.5 py-2 text-left transition-all',
        'w-[72px] shrink-0 sm:w-20',
        selected
          ? // Selected: thicker solid acid border + acid CTA shadow.
            // The acid-tint fill went chartreuse on light surfaces; the
            // border + shadow combo carries the "active" cue without the
            // muddy fill. The tile bg stays paper.
            'border-2 border-acid bg-bg-2 shadow-[var(--shadow-acid-cta)]'
          : `${accentClasses[state]} hover:border-paper-dim`,
        // Today gets a hairline acid ring when not selected so "where am
        // I now" is always visible. The `!selected` guard hands the
        // selected branch its 2px border without competing.
        isToday && !selected && 'border-acid'
      )}
    >
      <div className="flex items-start justify-between">
        <span
          className={cn(
            'mono text-[10px] tracking-[0.18em] uppercase',
            isToday ? 'font-semibold text-paper' : selected ? 'text-paper' : 'text-paper-faint'
          )}
        >
          {formatDayLabel(date)}
        </span>
        <StatusIndicator state={state} pendingCount={pendingCount} readyCount={readyCount} />
      </div>

      <span
        className={cn(
          'num-em text-[20px] leading-none',
          state === 'empty' ? 'text-paper-faint' : 'text-paper'
        )}
      >
        {formatDayNumber(date)}
      </span>
    </button>
  )
}

function StatusIndicator({
  state,
  pendingCount,
  readyCount,
}: {
  state: TileState
  pendingCount: number
  readyCount: number
}) {
  // T3 indicators. Pending → warning amber (calls for action). Approved /
  // live → success green check. Drafting → acid pulsing dot ("AI is working").
  // Skipped → faint grey dot. Empty → nothing.
  if (state === 'needs_review') {
    return (
      <span
        aria-hidden
        className="text-ink-on-acid mono flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[color:var(--warning)] px-1 text-[10px] font-bold"
      >
        {pendingCount}
      </span>
    )
  }
  if (state === 'approved' || state === 'live') {
    return (
      <span
        aria-hidden
        className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[color:var(--success)] px-1 text-white"
        title={`${readyCount} ready`}
      >
        <Check size={11} strokeWidth={3} />
      </span>
    )
  }
  if (state === 'drafting' || state === 'expected') {
    // Drafting is "AI working" → acid pulsing dot. Same animation as .liveDot.
    return <span aria-hidden className="liveDot" />
  }
  if (state === 'skipped') {
    return <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-paper-faint" />
  }
  return null
}
